import { Node, mergeAttributes } from '@tiptap/react'
import {
	getSize,
	getStyleStyles,
	getLayoutStyles,
	getPositionStyles,
} from '#app/utils/styling.js'

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		frame: {
			setFrame: () => ReturnType
			updatePosition: (position: FramePosition) => ReturnType
			updateSize: (size: FrameSize) => ReturnType
			updateLayout: (layout: FrameLayout) => ReturnType
			updateStyle: (style: FrameStyle) => ReturnType
			resetAll: () => ReturnType
			resetSize: () => ReturnType
			resetPosition: () => ReturnType
			resetLayout: () => ReturnType
			resetStyle: () => ReturnType
		}
	}
}

export type FramePosition = {
	top: string
	bottom: string
	left: string
	right: string
	type: 'sticky' | 'relative' | 'absolute' | 'fixed'
}

export type FrameSize = {
	width: 'fill' | 'fixed' | 'fit' | 'relative'
	height: 'fill' | 'fixed' | 'fit' | 'relative'
	widthValue?: string
	heightValue?: string
}

export type FrameLayout = {
	direction: 'row' | 'column'
	distribute:
		| 'center'
		| 'flex-start'
		| 'flex-end'
		| 'space-around'
		| 'space-between'
	align: 'flex-start' | 'center' | 'flex-end' | 'baseline' | 'end' | 'normal'
	wrap: 'yes' | 'no'
	gap: number
	paddingLeft: number
	paddingRight: number
	paddingTop: number
	paddingBottom: number
}

export type FrameStyle = {
	backgroundColor: string
	radiusTopLeft: number
	radiusTopRight: number
	radiusBottomRight: number
	radiusBottomLeft: number
	border: string
	shadow: string
}

export const FrameBlock = Node.create({
	name: 'frame',

	isolating: true,

	content: 'block',

	defining: true,

	group: 'block',

	draggable: true,

	selectable: true,

	addAttributes() {
		return {
			position: {
				default: {
					top: '',
					bottom: '',
					left: '',
					right: '',
					type: 'relative',
				},
				parseHTML: element => ({
					top: element.style.top,
					bottom: element.style.bottom,
					left: element.style.left,
					right: element.style.right,
					type: element.style.position,
				}),
			},
			size: {
				default: {
					width: 'fill',
					height: 'fill',
					widthValue: '',
					heightValue: '',
				},
				parseHTML: element => ({
					width: element.style.width,
					height: element.style.height,
					widthValue: element.style.width.replace('%', '').replace('px', ''),
					heightValue: element.style.height.replace('%', '').replace('px', ''),
				}),
			},
			layout: {
				default: {
					direction: 'row',
					distribute: 'space-between',
					align: 'normal',
					wrap: 'no',
					gap: 0,
					paddingLeft: 0,
					paddingRight: 0,
					paddingTop: 0,
					paddingBottom: 0,
				},
				parseHTML: element => ({
					direction: element.style.flexDirection,
					distribute: element.style.justifyContent,
					align: element.style.alignItems,
					wrap: element.style.flexWrap === 'wrap' ? 'yes' : 'no',
					gap: parseInt(element.style.gap, 10),
					paddingLeft: parseInt(element.style.paddingLeft, 10),
					paddingRight: parseInt(element.style.paddingRight, 10),
					paddingTop: parseInt(element.style.paddingTop, 10),
					paddingBottom: parseInt(element.style.paddingBottom, 10),
				}),
			},
			style: {
				default: {
					backgroundColor: '',
					radiusTopLeft: 0,
					radiusTopRight: 0,
					radiusBottomRight: 0,
					radiusBottomLeft: 0,
					border: '',
					shadow: '',
				},
				parseHTML: element => ({
					backgroundColor: element.style.backgroundColor,
					borderRadius: element.style.borderRadius,
					border: element.style.border,
					shadow: element.style.boxShadow,
				}),
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: 'div[data-type="frame"]',
			},
		]
	},

	renderHTML({ HTMLAttributes }) {
		const { position = {}, size = {}, layout = {}, style = {} } = HTMLAttributes

		const styles = {
			...getSize(size),
			...getStyleStyles(style),
			...getLayoutStyles(layout),
			...getPositionStyles(position),
		}

		const styleString = Object.entries(styles)
			.map(([key, value]) => `${key}: ${value}`)
			.join('; ')

		return [
			'div',
			mergeAttributes({
				style: styleString,
				'data-type': 'frame',
			}),
			0,
		]
	},

	addCommands() {
		return {
			setFrame:
				() =>
				({ commands }) => {
					return commands.insertContent(
						`<div data-type="frame"><p>Tap the 6 little dots to adjust your frame</p></div>`,
					)
				},
			updateSize:
				size =>
				({ commands }) => {
					return commands.updateAttributes('frame', { size })
				},
			updatePosition:
				position =>
				({ commands }) => {
					return commands.updateAttributes('frame', { position })
				},
			updateLayout:
				layout =>
				({ commands }) => {
					return commands.updateAttributes('frame', { layout })
				},
			updateStyle:
				style =>
				({ commands }) => {
					return commands.updateAttributes('frame', { style })
				},
		}
	},
})
